/**
 * プロジェクト詳細ビュー（インライン）。
 * プロジェクト一覧カードをクリックすると、MailListPage 内でこのビューに切り替わる。
 * - プロジェクトに所属するメール一覧表示
 * - メール追加（MailPickerDialog 呼び出し）
 * - 各メールをプロジェクトから外す
 */
import { ArrowLeft, Mail as MailIcon, Plus, X } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useMails,
  useUpdateMail,
  type MailProjectRecord,
  type MailRecord,
} from '@/hooks/useMailData';
import { formatDate } from '@/lib/firestoreTime';

import { MailDetailDialog } from './MailListPage';
import { MailPickerDialog } from './MailPickerDialog';

interface ProjectDetailViewProps {
  project: MailProjectRecord;
  onBack: () => void;
}

export function ProjectDetailView({ project, onBack }: ProjectDetailViewProps) {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data: mails, isLoading, refetch } = useMails({
    deleted: false,
    projectId: project.id,
    limit: 500,
  });
  const updateMutation = useUpdateMail();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [openMail, setOpenMail] = useState<MailRecord | null>(null);

  const handleRemove = async (mailId: string, subject: string) => {
    if (!window.confirm(`「${subject}」をプロジェクトから外します。`)) return;
    try {
      const mail = mails?.find((m) => m.id === mailId);
      const next = (mail?.projectIds ?? []).filter((p) => p !== project.id);
      await updateMutation.mutateAsync({ id: mailId, data: { projectIds: next } });
      toast.success('プロジェクトから外しました');
      await refetch();
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft />
            プロジェクト一覧
          </Button>
          <div className="flex items-center gap-2">
            <div
              className="size-4 rounded-full flex-shrink-0"
              style={{ background: project.color }}
            />
            <h3 className="font-semibold text-base">{project.name}</h3>
            <span className="text-xs text-[var(--text-muted)]">
              作成日: {formatDate(project.createdAt)}
            </span>
          </div>
        </div>
        {canEdit && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => setPickerOpen(true)}
          >
            <Plus />
            メール追加
          </Button>
        )}
      </div>

      <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden min-h-[200px]">
        {isLoading ? (
          <div className="p-8">
            <Spinner />
          </div>
        ) : !mails || mails.length === 0 ? (
          <div className="p-12 text-center">
            <MailIcon className="size-10 text-[var(--text-muted)] mx-auto mb-3" />
            <p className="text-sm text-[var(--text-muted)]">
              このプロジェクトにはまだメールがありません
            </p>
            {canEdit && (
              <p className="text-xs text-[var(--text-muted)] mt-1">
                右上「メール追加」から既存メールを割り当てできます
              </p>
            )}
          </div>
        ) : (
          mails.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-3 p-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
              onClick={() => setOpenMail(m)}
            >
              <MailIcon className="size-4 text-[var(--text-muted)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium truncate">
                    {m.subject || '(件名なし)'}
                  </span>
                  <Badge
                    variant={
                      m.status === 'sent'
                        ? 'sent'
                        : m.status === 'approved'
                          ? 'approved'
                          : m.status === 'review'
                            ? 'review'
                            : 'draft'
                    }
                  >
                    {m.status}
                  </Badge>
                </div>
                <div className="text-xs text-[var(--text-muted)]">{formatDate(m.createdAt)}</div>
              </div>
              {canEdit && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRemove(m.id, m.subject || m.id);
                  }}
                  title="このプロジェクトから外す"
                >
                  <X />
                </Button>
              )}
            </div>
          ))
        )}
      </div>

      <MailPickerDialog
        open={pickerOpen}
        onOpenChange={(o) => {
          setPickerOpen(o);
          if (!o) void refetch();
        }}
        projectId={project.id}
        projectName={project.name}
      />

      {openMail && <MailDetailDialog mail={openMail} onClose={() => setOpenMail(null)} />}
    </div>
  );
}
